import { Injectable, Inject } from '@angular/core';
import merge from 'deepmerge';
import { BehaviorSubject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { ICustomDimensions } from './interfaces';

/**
 * A service used for managing objects that contain analytic dimensions as key value pairs and
 * that pushing those objects to Google Tag Manager. If it's impossible to send the data structured as
 * desired using the Tag Manager library service, this service provides a way to do so.
 *
 * This service is built on top of Google Tag Manager's `dataLayer`. More information about the Data Layer is
 * available [here](https://developers.google.com/tag-manager/devguide).
 *
 * This Data Layer service allows for building up an object in it's local state object (E.g. storing dimensions
 * about a user as their information is retrieved) and then for pushing that state object on to the
 * `dataLayer`. Note, it's possible to set default dimensions that are always sent to the `dataLayer` using
 * the library's `forRoot` options when importing the module, but these will be pushed *every time data is
 * sent to Tag Manger, including with the library's built in methods.
 *
 * Note that this service's state is not cleared after calling `push()`.
 *
 */
@Injectable()
export class TagMangerDataLayerService {
    /** @ignore The subject that keeps track of the channel's current state. */
    private _dimensions: BehaviorSubject<ICustomDimensions>;
    /** @ignore The default values the state will always be set back to when reset. */
    private _defaults: ICustomDimensions;

    /**
   * Creates an instance of TagMangerDataLayerService.
   *
   * @param _defaultDimensions Default dimensions that will be set on each push. Provided by calling the
   * `forRoot()` method when importing the library and supplying it's default dimensions parameter.
   */
    constructor (
        @Inject('NGX_TAG_MANAGER_DEFAULT_DIMENSIONS') private _defaultDimensions: ICustomDimensions
    ) {
        this._defaults = _defaultDimensions || {};
        this._dimensions = new BehaviorSubject(this._defaults);
    }

    /**
   * Get an observable of the current dimensions in the service's store.
   * @example
   * this.dimensions$.pipe( take(1) ).subscribe( (dimensions) => (<any>window).dataLayer.push(this.dimensions) ) );
  */
    public get dimensions$ (): Observable<ICustomDimensions> {
        return this._dimensions.asObservable();
    }

    /**
   * Set a new value for the dimensions saved in the service's store, completely overriding the current values.
   * If there are certain defaults to always be sent and kept populated (e.g. something like an `appName`
   * dimension) or if an object is being built up as data becomes available for pushing to Tag Manger later,
   * use `update()` instead. Otherwise, those values may be destroyed or remain unsent.
   *
   * @param newDimensions The set of dimensions as key value pairs that will override the existing state.
   *
   * @example _dataLayerService.dimensions = {userId: '123'};
   */
    set dimensions (newDimensions: ICustomDimensions) {
        this._dimensions.next(newDimensions);
    }

    /**
   * Reset the dimensions contained in the service's store to the defaults (if provided), destroying
   * any currently awaiting a call to `push()`.
   *
   * @example _dataLayerService.clear();
   */
    clear () {
        this.dimensions = this._defaults;
    }

    /**
   * Take a new set of dimensions and merge them into the current dimensions in the service's store.
   *
   * For use when some default dimensions are set that should be preserved (e.g. an `appName` dimension) or
   * a set of dimensions needs to be built up over multiple calls before it all the data is available and
   * `push()` can be called.
   *
   * @param newDimensions The dimensions that will be merged with the current store.
   *
   * @example _dataLayerService.update({userId: '123'});
   */
    update (newDimensions: ICustomDimensions) {
        this.dimensions = merge(this._dimensions, newDimensions);
    }

    /**
   * Take any dimensions in the service's store and push them to the Google `dataLayer`, causing the values
   * to be sent in a `message` to Google Tag Manager.
   *
   * @example _dataLayerService.push();
   */
    push () {
        this.dimensions$
            .pipe(take(1))
            .subscribe(
                (payloadDimensions: ICustomDimensions) => {
                    (<any>window).dataLayer.push(payloadDimensions);
                }
            );
    }
}
